import { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import { Icon, PopOverCustomMenuType, PopOverItemType } from 'components'
import { WidthProps } from 'types/styled'
import { Z_INDEX } from 'utils/constants'

// TODO: Use correct icons and set SHOW_TEXT_OPTIONS to true when they become available
const SHOW_TEXT_OPTIONS = false
const TEXT_OPTIONS: PopOverItemType<undefined>[] = [
  {
    icon: 'plus',
    label: 'Wrap Text',
    value: 'wrapText',
  },
  {
    icon: 'plus',
    label: 'Truncate Text',
    value: 'truncateText',
  },
]

type MoreOptionsProps = PopOverCustomMenuType & {
  onSelect: (value: PopOverItemType<{ isSelected: boolean } | undefined>) => void
  textOptionValue?: 'truncateText' | 'wrapText'
  width?: WidthProps
  nonExpandableOptions: Array<PopOverItemType<{ isSelected: boolean }>>
}

const MoreOptions = ({
  onSelect,
  onToggleOpenMenu,
  textOptionValue = 'wrapText',
  width = 'auto',
  nonExpandableOptions,
}: MoreOptionsProps) => {
  const { t } = useTranslation()
  const [showTextSubmenu, setShowTextSubmenu] = useState(false)

  const handleSelect = (value: PopOverItemType<{ isSelected: boolean } | undefined>) => {
    if (typeof onToggleOpenMenu === 'function') onToggleOpenMenu()
    setShowTextSubmenu(false)
    onSelect(value)
  }

  return (
    <Container width={width}>
      {nonExpandableOptions.map((o, index) => (
        <StyledOption
          key={index}
          onClick={() => {
            handleSelect(o)
          }}
        >
          {o?.icon && <OptionIcon icon={o.icon} />}
          {o?.label && <LabelContainer>{t(o.label)}</LabelContainer>}
          {o?.data?.isSelected && <Icon icon={'check'} />}
          {o?.node}
        </StyledOption>
      ))}
      {SHOW_TEXT_OPTIONS && (
        <>
          <StyledOption
            isExpanded={showTextSubmenu}
            onClick={() => {
              setShowTextSubmenu(!showTextSubmenu)
            }}
          >
            <OptionIcon icon="align-justify" />
            <LabelContainer>{t('Text')}</LabelContainer>
            <StyledIcon icon={showTextSubmenu ? 'chevron-up' : 'chevron-down'} />
          </StyledOption>
          {showTextSubmenu &&
            TEXT_OPTIONS.map((o, index) => (
              <SubOption
                key={index}
                onClick={() => {
                  handleSelect(o)
                }}
              >
                {o.icon && <OptionIcon icon={o.icon} />}
                <LabelContainer>{t(o.label)}</LabelContainer>
                {textOptionValue === o.value && <StyledIcon color="green" icon="check" />}
              </SubOption>
            ))}
        </>
      )}
    </Container>
  )
}

const Container = styled.div<{ width?: WidthProps }>`
  min-width: 222px;
  width: ${(p) => (!p.width || p.width === 'auto' ? '100%' : `${p.width}px`)};
`

const LabelContainer = styled.div`
  ${(p) => p.theme.font({ size: 14, weight: 'normal' })}
  align-items: center;
  display: flex;
  flex-grow: 1;
  line-height: 24px;
  padding: 3px 0;
`

const StyledIcon = styled((p) => <Icon {...p} />)``

const OptionIcon = styled((p) => <StyledIcon {...p} />)`
  margin-right: 10px;
`

const StyledOption = styled.div<{ isExpanded?: boolean }>`
  background: ${(p) => (p.isExpanded ? p.theme.colors.gray1 : p.theme.colors.white)};
  cursor: pointer;
  display: flex;
  padding: 10px 13px;
  text-align: left;
  user-select: none;
  z-index: ${Z_INDEX.dropdown};
  align-items: center;

  &:hover {
    background: ${(p) => p.theme.colors.gray1};
  }
`

const SubOption = styled(StyledOption)`
  background: ${(p) => p.theme.colors.gray1};
  padding-left: 43px;
`

export default MoreOptions
